import React, { useEffect, useMemo, useState } from 'react'
import { Trash2, Upload } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi, type UpdateMePayload } from '../../api/auth'
import { useAuthStore } from '../../store/authStore'
import { invalidateUserPresentationViews } from '../../lib/queryInvalidation'
import { Avatar } from '../common/Avatar'
import { Button } from '../common/Button'
import { Modal } from '../common/Modal'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  outline: 'none',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '4px',
}

type Notice = {
  tone: 'success' | 'error'
  message: string
} | null

function noticeStyle(tone: 'success' | 'error'): React.CSSProperties {
  const colors = tone === 'success'
    ? { bg: '#ecfdf3', border: '#bbf7d0', color: '#166534' }
    : { bg: '#fef2f2', border: '#fecaca', color: '#991b1b' }

  return {
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    color: colors.color,
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '13px',
  }
}

export function ProfileSettingsModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { user, updateUser } = useAuthStore()
  const [fullName, setFullName] = useState(user?.fullName || '')
  const [email, setEmail] = useState(user?.email || '')
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [removeAvatar, setRemoveAvatar] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [notice, setNotice] = useState<Notice>(null)

  useEffect(() => {
    if (!isOpen || !user) return
    setFullName(user.fullName)
    setEmail(user.email)
    setAvatarFile(null)
    setRemoveAvatar(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setNotice(null)
  }, [isOpen, user])

  const avatarPreview = useMemo(() => {
    if (avatarFile) return URL.createObjectURL(avatarFile)
    if (!removeAvatar) return user?.avatarUrl || null
    return null
  }, [avatarFile, removeAvatar, user?.avatarUrl])

  useEffect(() => {
    return () => {
      if (avatarFile && avatarPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview)
      }
    }
  }, [avatarFile, avatarPreview])

  const profileMutation = useMutation({
    mutationFn: (payload: UpdateMePayload) => authApi.updateMe(payload),
    onSuccess: async (response) => {
      updateUser(response.data.data)
      await invalidateUserPresentationViews(queryClient)
      setAvatarFile(null)
      setRemoveAvatar(false)
      setNotice({ tone: 'success', message: 'Profile updated successfully.' })
    },
    onError: (error: any) => {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not update profile.',
      })
    },
  })

  const passwordMutation = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setNotice({ tone: 'success', message: 'Password updated successfully.' })
    },
    onError: (error: any) => {
      setNotice({
        tone: 'error',
        message: error.response?.data?.error || 'Could not update password.',
      })
    },
  })

  if (!user) return null

  const saveProfile = () => {
    profileMutation.mutate({
      fullName: fullName.trim(),
      email: email.trim().toLowerCase(),
      avatar: avatarFile,
      removeAvatar,
    })
  }

  const savePassword = () => {
    if (!currentPassword || !newPassword) {
      setNotice({ tone: 'error', message: 'Please fill in both password fields.' })
      return
    }
    if (newPassword !== confirmPassword) {
      setNotice({ tone: 'error', message: 'New password and confirm password do not match.' })
      return
    }
    passwordMutation.mutate()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="My Profile" width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {notice && <div style={noticeStyle(notice.tone)}>{notice.message}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Avatar user={{ fullName: fullName || user.fullName, avatarUrl: avatarPreview || undefined }} size="lg" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={labelStyle}>Profile Photo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <label
                style={{
                  ...inputStyle,
                  width: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <Upload size={14} />
                <span>{avatarFile ? 'Change Photo' : 'Upload Photo'}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null
                    setAvatarFile(file)
                    setRemoveAvatar(false)
                  }}
                  style={{ display: 'none' }}
                />
              </label>
              {(avatarPreview || user.avatarUrl) && (
                <button
                  type="button"
                  onClick={() => {
                    setAvatarFile(null)
                    setRemoveAvatar(true)
                  }}
                  style={{
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#dc2626',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              )}
            </div>
            <span style={{ fontSize: '12px', color: '#64748b' }}>PNG, JPG, GIF, or WEBP up to 5MB.</span>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Full Name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              loading={profileMutation.isPending}
              disabled={!fullName.trim() || !email.trim()}
              onClick={saveProfile}
            >
              Save Profile
            </Button>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '18px', display: 'grid', gap: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', color: '#1f2937' }}>Change Password</h3>
          <div>
            <label style={labelStyle}>Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              loading={passwordMutation.isPending}
              disabled={!currentPassword || !newPassword || !confirmPassword}
              onClick={savePassword}
            >
              Update Password
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
